import { useState, useRef, useEffect } from "react";

export const ALL_SOURCES = [
  { value: "public_website", label: "Website",      icon: "ri-global-line" },
  { value: "ai_assistant",   label: "AI Assistant", icon: "ri-robot-line" },
  { value: "contact_form",   label: "Contact Form", icon: "ri-mail-line" },
  { value: "invitation",     label: "Invitation",   icon: "ri-gift-line" },
  { value: "manual",         label: "Manual",       icon: "ri-add-circle-line" },
  { value: "other",          label: "Other",        icon: "ri-question-line" },
];

export const EVENT_TYPES = [
  "Wedding", "XV Años", "Baptism", "Graduation", "Birthday",
  "Baby Shower", "Engagement", "Anniversary", "Corporate Event", "Portrait", "Other",
];

export interface AdvancedFilters {
  source:     string;   // "" = all
  event_type: string;   // "" = all
  date_from:  string;   // ISO date string or ""
  date_to:    string;
}

interface Props {
  filters: AdvancedFilters;
  onChange: (f: AdvancedFilters) => void;
  activeCount: number; // number of non-default filters
}

export default function LeadsFilters({ filters, onChange, activeCount }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const set = <K extends keyof AdvancedFilters>(key: K, val: AdvancedFilters[K]) =>
    onChange({ ...filters, [key]: val });

  const clearAll = () =>
    onChange({ source: "", event_type: "", date_from: "", date_to: "" });

  const INPUT = "w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-300 outline-none focus:border-[#C8A96E]/60 focus:bg-white transition-all cursor-pointer";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
          activeCount > 0
            ? "bg-[#C8A96E]/10 border-[#C8A96E]/30 text-[#9a7840]"
            : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
        }`}
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-equalizer-3-line text-sm"></i>
        </span>
        Filters
        {activeCount > 0 && (
          <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#C8A96E] text-white text-[9px] font-bold">
            {activeCount}
          </span>
        )}
        <span className="w-4 h-4 flex items-center justify-center">
          <i className={`text-xs transition-transform ${open ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}></i>
        </span>
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-2xl border border-slate-200 shadow-lg z-30 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="text-slate-700 text-sm font-semibold">Advanced Filters</span>
            {activeCount > 0 && (
              <button
                onClick={clearAll}
                className="text-xs text-slate-400 hover:text-red-500 transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-close-line text-sm"></i>
                Clear all
              </button>
            )}
          </div>

          <div className="p-4 space-y-4">
            {/* Source */}
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2 block">Source</label>
              <div className="grid grid-cols-2 gap-1.5">
                <button
                  onClick={() => set("source", "")}
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-all cursor-pointer ${
                    !filters.source
                      ? "bg-[#C8A96E]/10 border-[#C8A96E]/30 text-[#9a7840] font-medium"
                      : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300"
                  }`}
                >
                  <i className="ri-list-check-2 text-[11px]"></i>
                  All sources
                </button>
                {ALL_SOURCES.map((s) => (
                  <button
                    key={s.value}
                    onClick={() => set("source", filters.source === s.value ? "" : s.value)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs border transition-all cursor-pointer ${
                      filters.source === s.value
                        ? "bg-[#C8A96E]/10 border-[#C8A96E]/30 text-[#9a7840] font-medium"
                        : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-300"
                    }`}
                  >
                    <i className={`${s.icon} text-[11px]`}></i>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Event type */}
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2 block">Event Type</label>
              <select
                value={filters.event_type}
                onChange={(e) => set("event_type", e.target.value)}
                className={INPUT}
              >
                <option value="">All event types</option>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>

            {/* Date range */}
            <div>
              <label className="text-slate-500 text-xs font-medium uppercase tracking-wider mb-2 block">Date Range</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 text-[10px] mb-1 block">From</label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => set("date_from", e.target.value)}
                    className={INPUT}
                  />
                </div>
                <div>
                  <label className="text-slate-400 text-[10px] mb-1 block">To</label>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => set("date_to", e.target.value)}
                    min={filters.date_from || undefined}
                    className={INPUT}
                  />
                </div>
              </div>
              {filters.date_from && filters.date_to && (
                <p className="text-slate-400 text-[10px] mt-1.5 text-center">
                  {new Date(filters.date_from).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                  {" — "}
                  {new Date(filters.date_to).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </p>
              )}
            </div>
          </div>

          {/* Apply button */}
          <div className="px-4 py-3 border-t border-slate-100">
            <button
              onClick={() => setOpen(false)}
              className="w-full py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}